import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Edit2, Save, X, Loader2, Search, Filter, ChevronDown, ChevronUp, ChevronsUpDown, Globe, Video, Mic, Star, RefreshCw, MoreHorizontal, Eye, EyeOff, Youtube } from 'lucide-react';
import DriveUploader from '../components/vod/DriveUploader';

const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

interface Channel {
  cuid: string;
  name: string;
  description: string;
  logo_url: string;
  banner_url: string;
  created_at: string;
  difficulty_level?: string;
  publish_status?: string;
  audio_language?: string;
  cast_members?: string;
  release_year?: number;
  country?: string;
  original_language?: string;
  rating?: string;
  director?: string;
  writer?: string;
  youtube_channel_id?: string;
}

type SortField = 'name' | 'original_language' | 'publish_status' | 'country' | 'release_year' | 'created_at' | 'difficulty_level';
type SortDir = 'asc' | 'desc';

const LANGUAGES = [
  { value: '', label: 'All Languages' },
  { value: 'Arabic', label: 'العربية' },
  { value: 'English', label: 'English' },
  { value: 'French', label: 'Français' },
  { value: 'Spanish', label: 'Español' },
  { value: 'Dutch', label: 'Nederlands' },
  { value: 'German', label: 'Deutsch' },
  { value: 'Hebrew', label: 'עברית' },
  { value: 'Moroccan Darija', label: 'الدارجة' },
  { value: 'Amazigh', label: 'الأمازيغية' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status', icon: null },
  { value: 'published', label: 'Published', icon: Eye, color: 'text-emerald-600 bg-emerald-50' },
  { value: 'draft', label: 'Draft', icon: EyeOff, color: 'text-amber-600 bg-amber-50' },
  { value: 'archived', label: 'Archived', icon: EyeOff, color: 'text-slate-600 bg-slate-100' },
];

const initialFormData = {
  name: '', description: '', logo_url: '', banner_url: '',
  difficulty_level: '', publish_status: 'published', audio_language: '', cast_members: '',
  release_year: '', country: '', original_language: '', rating: '', director: '', writer: '', youtube_channel_id: ''
};

export default function VodChannels() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCuid, setEditingCuid] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState(initialFormData);

  const [search, setSearch] = useState('');
  const [filterLang, setFilterLang] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const fetchChannels = async () => {
    try {
      const res = await fetch(`${API_URL}/content/channels`);
      if (res.ok) {
        const data = await res.json();
        setChannels(data);
      }
    } catch (err) {
      console.error('Failed to fetch channels:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChannels(); }, []);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown size={14} className="text-slate-300" />;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const filtered = useMemo(() => {
    let list = [...channels];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.director?.toLowerCase().includes(q) ||
        c.writer?.toLowerCase().includes(q) ||
        c.youtube_channel_id?.toLowerCase().includes(q)
      );
    }
    if (filterLang) list = list.filter(c => c.original_language === filterLang);
    if (filterStatus) list = list.filter(c => c.publish_status === filterStatus);
    if (filterCountry) list = list.filter(c => c.country === filterCountry);

    list.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') return (aVal - bVal) * dir;
      return String(aVal).localeCompare(String(bVal)) * dir;
    });

    return list;
  }, [channels, search, filterLang, filterStatus, filterCountry, sortField, sortDir]);

  const handleOpenModal = (channel?: Channel) => {
    if (channel) {
      setEditingCuid(channel.cuid);
      setFormData({
        name: channel.name,
        description: channel.description || '',
        logo_url: channel.logo_url || '',
        banner_url: channel.banner_url || '',
        difficulty_level: channel.difficulty_level || '',
        publish_status: channel.publish_status || 'published',
        audio_language: channel.audio_language || '',
        cast_members: channel.cast_members || '',
        release_year: channel.release_year?.toString() || '',
        country: channel.country || '',
        original_language: channel.original_language || '',
        rating: channel.rating || '',
        director: channel.director || '',
        writer: channel.writer || '',
        youtube_channel_id: channel.youtube_channel_id || ''
      });
    } else {
      setEditingCuid(null);
      setFormData(initialFormData);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => { setIsModalOpen(false); setEditingCuid(null); };

  const handleSave = async () => {
    if (!formData.name.trim()) return alert('Name is required');
    setSaving(true);
    const method = editingCuid ? 'PUT' : 'POST';
    const url = editingCuid ? `${API_URL}/content/channels/${editingCuid}` : `${API_URL}/content/channels`;
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (res.ok) { await fetchChannels(); handleCloseModal(); }
      else { alert('Failed to save channel'); }
    } catch (err) { console.error(err); alert('Error saving channel'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (cuid: string) => {
    if (!confirm('Are you sure you want to delete this channel?')) return;
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API_URL}/content/channels/${cuid}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setChannels(channels.filter(c => c.cuid !== cuid));
    } catch (err) { console.error(err); alert('Error deleting channel'); }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    if (!confirm(`Delete ${selectedRows.size} channel(s)?`)) return;
    for (const cuid of selectedRows) await handleDelete(cuid);
    setSelectedRows(new Set());
  };

  const handleSync = async (cuid: string) => {
    setSyncingIds(prev => new Set(prev).add(cuid));
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API_URL}/content/channels/${cuid}/sync`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { const data = await res.json(); alert(`Imported ${data.imported} videos.`); }
      else { const err = await res.json(); alert('Sync failed: ' + err.error); }
    } catch (err) { console.error(err); alert('Error syncing channel'); }
    finally { setSyncingIds(prev => { const n = new Set(prev); n.delete(cuid); return n; }); }
  };

  const toggleRow = (cuid: string) => {
    const next = new Set(selectedRows);
    if (next.has(cuid)) next.delete(cuid); else next.add(cuid);
    setSelectedRows(next);
  };

  const toggleAll = () => {
    if (selectedRows.size === filtered.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(filtered.map(c => c.cuid)));
  };

  const countries = useMemo(() => {
    const set = new Set(channels.map(c => c.country).filter(Boolean));
    return Array.from(set).sort();
  }, [channels]);

  const StatusBadge = ({ status }: { status?: string }) => {
    if (!status || status === 'published') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"><Eye size={12} /> Published</span>;
    if (status === 'draft') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200"><EyeOff size={12} /> Draft</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200"><EyeOff size={12} /> Archived</span>;
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-indigo-500 w-8 h-8" /></div>;

  return (
    <div className="p-6 xl:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Channels & Narrators</h1>
          <p className="text-sm text-slate-500 mt-1">{channels.length} channels · {filtered.length} shown</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedRows.size > 0 && (
            <button onClick={handleBulkDelete} className="px-4 py-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-sm font-medium hover:bg-rose-100 transition-colors flex items-center gap-2">
              <Trash2 size={16} /> Delete {selectedRows.size}
            </button>
          )}
          <button onClick={() => handleOpenModal()} className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-sm">
            <Plus size={18} /> Add Channel
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-2">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-slate-50"
              placeholder="Search by name, director, writer..."
            />
          </div>
          <select value={filterLang} onChange={e => setFilterLang(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-indigo-500 outline-none">
            {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-indigo-500 outline-none">
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-indigo-500 outline-none">
            <option value="">All Countries</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {(search || filterLang || filterStatus || filterCountry) && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <Filter size={14} className="text-slate-400" />
            <span className="text-xs text-slate-500">Filters active · </span>
            <button onClick={() => { setSearch(''); setFilterLang(''); setFilterStatus(''); setFilterCountry(''); }}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Clear all</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="p-4 w-10">
                  <input type="checkbox" checked={selectedRows.size === filtered.length && filtered.length > 0}
                    onChange={toggleAll} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                </th>
                <th className="p-4 text-left">
                  <button onClick={() => toggleSort('name')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Name <SortIcon field="name" />
                  </button>
                </th>
                <th className="p-4 text-left hidden md:table-cell">
                  <button onClick={() => toggleSort('original_language')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Language <SortIcon field="original_language" />
                  </button>
                </th>
                <th className="p-4 text-left hidden lg:table-cell">
                  <button onClick={() => toggleSort('publish_status')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Status <SortIcon field="publish_status" />
                  </button>
                </th>
                <th className="p-4 text-left hidden lg:table-cell">
                  <button onClick={() => toggleSort('country')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Country <SortIcon field="country" />
                  </button>
                </th>
                <th className="p-4 text-left hidden xl:table-cell">
                  <button onClick={() => toggleSort('release_year')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Year <SortIcon field="release_year" />
                  </button>
                </th>
                <th className="p-4 text-left hidden xl:table-cell">
                  <button onClick={() => toggleSort('difficulty_level')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Level <SortIcon field="difficulty_level" />
                  </button>
                </th>
                <th className="p-4 text-left hidden 2xl:table-cell">
                  <button onClick={() => toggleSort('created_at')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Created <SortIcon field="created_at" />
                  </button>
                </th>
                <th className="p-4 text-right w-32">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-12 text-center">
                    <div className="flex flex-col items-center text-slate-400">
                      <Video size={40} className="mb-3 opacity-50" />
                      <p className="font-medium text-slate-500">No channels found</p>
                      <p className="text-sm mt-1">Try adjusting your filters or create a new channel.</p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map(channel => (
                <tr key={channel.cuid} className={`hover:bg-slate-50 transition-colors group ${selectedRows.has(channel.cuid) ? 'bg-indigo-50/50' : ''}`}>
                  <td className="p-4">
                    <input type="checkbox" checked={selectedRows.has(channel.cuid)}
                      onChange={() => toggleRow(channel.cuid)}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 flex-shrink-0 shadow-sm">
                        {channel.logo_url && !channel.logo_url.includes('fake-r2-bucket.local') ? (
                          <img src={channel.logo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Mic size={16} className="text-slate-400" /></div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate max-w-[200px]">{channel.name}</p>
                        {channel.director && <p className="text-xs text-slate-500 truncate max-w-[200px]">{channel.director}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className="inline-flex items-center gap-1.5 text-slate-700">
                      <Globe size={14} className="text-slate-400" />
                      {channel.original_language || '—'}
                    </span>
                  </td>
                  <td className="p-4 hidden lg:table-cell">
                    <StatusBadge status={channel.publish_status} />
                  </td>
                  <td className="p-4 hidden lg:table-cell">
                    <span className="text-slate-700">{channel.country || '—'}</span>
                  </td>
                  <td className="p-4 hidden xl:table-cell">
                    <span className="text-slate-700">{channel.release_year || '—'}</span>
                  </td>
                  <td className="p-4 hidden xl:table-cell">
                    {channel.difficulty_level ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        channel.difficulty_level === 'Beginner' ? 'bg-emerald-50 text-emerald-700' :
                        channel.difficulty_level === 'Intermediate' ? 'bg-amber-50 text-amber-700' :
                        'bg-rose-50 text-rose-700'
                      }`}>
                        {channel.difficulty_level}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-4 hidden 2xl:table-cell">
                    <span className="text-slate-500 text-xs">
                      {channel.created_at ? new Date(channel.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleOpenModal(channel)}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit">
                        <Edit2 size={16} />
                      </button>
                      {channel.youtube_channel_id && (
                        <button onClick={() => handleSync(channel.cuid)} disabled={syncingIds.has(channel.cuid)}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50" title="Sync YouTube">
                          <RefreshCw size={16} className={syncingIds.has(channel.cuid) ? 'animate-spin' : ''} />
                        </button>
                      )}
                      <button onClick={() => handleDelete(channel.cuid)}
                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Delete">
                        <Trash2 size={16} />
                      </button>
                      {channel.youtube_channel_id && (
                        <a href={`https://www.youtube.com/channel/${channel.youtube_channel_id}`} target="_blank" rel="noopener noreferrer"
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Open YouTube">
                          <Youtube size={16} />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex justify-between items-center text-xs text-slate-500">
          <span>{filtered.length} of {channels.length} channels</span>
          <span>{selectedRows.size} selected</span>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                {editingCuid ? <Edit2 size={18} className="text-indigo-500" /> : <Plus size={18} className="text-indigo-500" />}
                {editingCuid ? 'Edit Channel' : 'Add Channel'}
              </h2>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-200 transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Channel Name *</label>
                  <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm"
                    placeholder="e.g. Disney Kids" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">YouTube Channel ID</label>
                  <input type="text" value={formData.youtube_channel_id} onChange={e => setFormData({...formData, youtube_channel_id: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm font-mono"
                    placeholder="e.g. UCX6OQ3DkcsbYNE6H8uQQuVA" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
                  <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none h-20 text-sm resize-none"
                    placeholder="Channel description..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Difficulty Level</label>
                  <select value={formData.difficulty_level} onChange={e => setFormData({...formData, difficulty_level: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
                    <option value="">Select Level</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Publish Status</label>
                  <select value={formData.publish_status} onChange={e => setFormData({...formData, publish_status: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Audio Language</label>
                  <input type="text" value={formData.audio_language} onChange={e => setFormData({...formData, audio_language: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="e.g. English, Arabic..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Original Language</label>
                  <select value={formData.original_language} onChange={e => setFormData({...formData, original_language: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
                    <option value="">Select Language...</option>
                    {LANGUAGES.filter(l => l.value).map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Release Year</label>
                  <input type="number" value={formData.release_year} onChange={e => setFormData({...formData, release_year: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="e.g. 2024" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Country</label>
                  <select value={formData.country} onChange={e => setFormData({...formData, country: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
                    <option value="">Select Country...</option>
                    <option value="Morocco">المغرب</option>
                    <option value="Egypt">مصر</option>
                    <option value="USA">الولايات المتحدة</option>
                    <option value="UK">المملكة المتحدة</option>
                    <option value="France">فرنسا</option>
                    <option value="Spain">إسبانيا</option>
                    <option value="Germany">ألمانيا</option>
                    <option value="Netherlands">هولندا</option>
                    <option value="Saudi Arabia">السعودية</option>
                    <option value="UAE">الإمارات</option>
                    <option value="Lebanon">لبنان</option>
                    <option value="Syria">سوريا</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Rating</label>
                  <input type="text" value={formData.rating} onChange={e => setFormData({...formData, rating: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="e.g. PG-13" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Director</label>
                  <input type="text" value={formData.director} onChange={e => setFormData({...formData, director: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="Director name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Writer</label>
                  <input type="text" value={formData.writer} onChange={e => setFormData({...formData, writer: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="Writer name" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Cast Members</label>
                  <input type="text" value={formData.cast_members} onChange={e => setFormData({...formData, cast_members: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="Comma separated list" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 pt-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Logo</label>
                  {formData.logo_url ? (
                    <div className="relative rounded-lg overflow-hidden border border-slate-200 aspect-square w-32 mb-2">
                      <img src={formData.logo_url} alt="Logo" className="w-full h-full object-cover" />
                      <button onClick={() => setFormData({...formData, logo_url: ''})}
                        className="absolute top-1 right-1 bg-rose-500 text-white p-1 rounded-md hover:bg-rose-600 transition-colors">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="mb-2">
                      <DriveUploader onUploadComplete={(url) => setFormData({...formData, logo_url: url})} />
                    </div>
                  )}
                  <input type="text" value={formData.logo_url} onChange={e => setFormData({...formData, logo_url: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="Or paste image URL" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Banner Image</label>
                  {formData.banner_url ? (
                    <div className="relative rounded-lg overflow-hidden border border-slate-200 aspect-video mb-2">
                      <img src={formData.banner_url} alt="Banner" className="w-full h-full object-cover" />
                      <button onClick={() => setFormData({...formData, banner_url: ''})}
                        className="absolute top-1 right-1 bg-rose-500 text-white p-1 rounded-md hover:bg-rose-600 transition-colors">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="mb-2">
                      <DriveUploader onUploadComplete={(url) => setFormData({...formData, banner_url: url})} />
                    </div>
                  )}
                  <input type="text" value={formData.banner_url} onChange={e => setFormData({...formData, banner_url: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="Or paste image URL" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button onClick={handleCloseModal}
                className="px-5 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-medium hover:bg-slate-100 transition-colors text-sm">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving || !formData.name.trim()}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 transition-colors text-sm shadow-sm">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {editingCuid ? 'Update Channel' : 'Create Channel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
